import React, { useMemo } from 'react';
import { Button, EllipsisSpan, Empty, Input, Tag } from '@maxtropy/components';
import { InputNumber, Select, Spin, Table } from 'antd';
import { uniqueId } from 'lodash-es';
import { dimensionUnitMap, ranges, rateOperatorOptions } from './constants';
import { formatDuration, formatLoadRate, getLoadRange } from './utils';
import styles from './index.module.scss';
import iconLoadLow from '../../../../../../imgs/icon-负载060@2x.png';
import iconLoadMid from '../../../../../../imgs/icon-负载6080@2x.png';
import iconLoadHigh from '../../../../../../imgs/icon-负载80100@2x.png';
import iconLoadOver from '../../../../../../imgs/icon-负载over100@2x.png';

const rangeColorMap: Record<string, string> = {
  low: '#16DD8E',
  mid: '#FFCB47',
  high: '#FF9247',
  over: '#FF477B',
};

const rangeIconMap: Record<string, string> = {
  low: iconLoadLow,
  mid: iconLoadMid,
  high: iconLoadHigh,
  over: iconLoadOver,
};

const getNameLabel = (activeTab: string) => {
  if (activeTab === 'line') return '回路名称/编号';
  if (activeTab === 'outlet') return '出口点位名称';
  if (activeTab === 'area') return '区域名称';
  if (activeTab === 'compressor') return '空压机名称';
  return '站房名称';
};

const getCardTitle = (activeTab: string) => `${dimensionUnitMap[activeTab] ?? '站房'}实时负载率统计`;

const RangeCards = ({ activeTab, activeRange, distributionData, onRangeClick, onRangeReset }: any) => {
  return (
    <div className={`${styles.cards} ${styles.distributionCards}`}>
      {ranges.map(item => (
        <div
          key={item.key}
          className={`${styles.distributionCard} ${styles.clickableCard} ${
            activeRange === item.key ? styles.selectedCard : ''
          }`}
          onClick={() => onRangeClick(item.key)}
          onDoubleClick={onRangeReset}
        >
          <img className={styles.distributionCardIcon} src={rangeIconMap[item.key]} alt="" />
          <div className={styles.distributionCardContent}>
            <div className={styles.distributionCardTitle}>{getCardTitle(activeTab)}</div>
            <div className={styles.distributionCardRange}>
              {item.label}
              <div className={styles.distributionCardValue}>
                {distributionData?.[item.countField as keyof typeof distributionData] ?? 0}个
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FilterToolbar = ({ activeTab, sourceList, filters, setters, nameOptions, gasSourceOptions }: any) => {
  const selectOptions = useMemo(() => {
    if (nameOptions?.length) return nameOptions;
    return sourceList.map((item: any) => ({ label: item.name, value: item.name }));
  }, [nameOptions, sourceList]);

  return (
    <div className={styles.toolbar}>
      <div className={styles.field}>
        <span>{getNameLabel(activeTab)}:</span>
        <Select
          allowClear
          showSearch
          value={filters.name}
          placeholder="请选择"
          options={selectOptions}
          style={{ width: 160 }}
          optionFilterProp="label"
          popupClassName={styles.selectDropdown}
          onChange={setters.setName}
        />
      </div>
      {activeTab === 'line' && (
        <>
          <div className={styles.field}>
            <span>起点:</span>
            <Input
              placeholder="请输入"
              style={{ width: 160 }}
              onChange={e => {
                setters.setStartPoint(e.target.value);
              }}
            />
          </div>
          <div className={styles.field}>
            <span>终点:</span>
            <Input placeholder="请输入" style={{ width: 160 }} onChange={e => setters.setEndPoint(e.target.value)} />
          </div>
        </>
      )}
      {['outlet', 'area'].includes(activeTab) && (
        <div className={styles.field}>
          <span>气体来源:</span>
          <Select
            allowClear
            showSearch
            value={filters.gasSource}
            placeholder="请选择"
            options={gasSourceOptions}
            style={{ width: 150 }}
            optionFilterProp="label"
            popupClassName={styles.selectDropdown}
            onChange={setters.setGasSource}
          />
        </div>
      )}
      <div className={styles.field}>
        <span>负载率:</span>
        <div className={styles.rateSearch}>
          <Select
            value={filters.rateOperator}
            options={rateOperatorOptions}
            style={{ width: 60 }}
            popupClassName={styles.selectDropdown}
            onChange={setters.setRateOperator}
          />
          <InputNumber
            min={0}
            value={filters.rateValue}
            placeholder="请输入"
            style={{ width: 130 }}
            onChange={setters.setRateValue}
          />
          <span className={styles.rateSuffix}>%</span>
        </div>
      </div>
    </div>
  );
};

const DistributionOverview = ({
  activeTab,
  activeRange,
  distributionData,
  loading,
  sourceList,
  filteredList,
  nameOptions,
  gasSourceOptions,
  filters,
  setters,
  onRangeClick,
  onRangeReset,
  onDetail,
}: any) => {
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        title: dimensionUnitMap[activeTab] ? `${dimensionUnitMap[activeTab]}名称` : '名称',
        dataIndex: 'name',
        width: 240,
        fixed: 'left' as const,
        render: (value: string) => <EllipsisSpan className={styles.nameCell} value={value} />,
      },
    ];

    if (activeTab === 'line') {
      baseColumns.push(
        {
          title: '回路起点',
          dataIndex: 'circuitStart',
          width: 100,
          render: (value: string) => <EllipsisSpan className={styles.nameCell} value={value} />,
        },
        {
          title: '回路终点',
          dataIndex: 'circuitEnd',
          width: 100,
          render: (value: string) => <EllipsisSpan className={styles.nameCell} value={value} />,
        }
      );
    }

    if (['outlet', 'area'].includes(activeTab)) {
      baseColumns.push({
        title: '气体来源',
        dataIndex: 'gasSource',
        width: 100,
        render: (value: string) => <EllipsisSpan className={styles.nameCell} value={value} />,
      });
    }

    return [
      ...baseColumns,
      {
        title: '实时负载率',
        dataIndex: 'realtimeLoadRate',
        width: 100,
        render: (value: number) => (
          <span style={{ color: rangeColorMap[getLoadRange(value)] }}>{formatLoadRate(value)}</span>
        ),
      },
      {
        title: '平均负载率',
        dataIndex: 'avgLoadRate',
        width: 90,
        render: (value: number) => <EllipsisSpan className={styles.nameCell} value={formatLoadRate(value)} />,
      },
      {
        title: '最大负载率',
        dataIndex: 'maxLoadRate',
        width: 90,
        render: (value: number) => <EllipsisSpan className={styles.nameCell} value={formatLoadRate(value)} />,
      },
      {
        title: '最小负载率',
        dataIndex: 'minLoadRate',
        width: 90,
        render: (value: number) => <EllipsisSpan className={styles.nameCell} value={formatLoadRate(value)} />,
      },
      {
        title: (
          <>
            负载率持续时长&nbsp;
            {ranges.map(item => (
              <Tag key={item.key} border="solid" color={`${rangeColorMap[item.key]}`}>
                {item.label}
              </Tag>
            ))}
          </>
        ),
        dataIndex: 'rangeDurations',
        width: 500,
        render: (durations: any[]) => (
          <EllipsisSpan
            value={ranges.map(range => {
              const item = durations?.find(duration => duration.range === range.key);
              return (
                <Tag key={range.key} border="solid" color={`${rangeColorMap[range.key]}`}>
                  {formatDuration(item?.duration, item?.durationPercent)}
                </Tag>
              );
            })}
          />
        ),
      },
      {
        title: '操作',
        dataIndex: 'action',
        width: 80,
        fixed: 'right' as const,
        render: (_: unknown, record: any) => (
          <Button type="link" style={{ padding: 0 }} onClick={() => onDetail(record)}>
            详情
          </Button>
        ),
      },
    ];
  }, [activeTab, onDetail]);

  return (
    <Spin spinning={loading}>
      <RangeCards
        activeTab={activeTab}
        activeRange={activeRange}
        distributionData={distributionData}
        onRangeClick={onRangeClick}
        onRangeReset={onRangeReset}
      />
      <FilterToolbar
        activeTab={activeTab}
        sourceList={sourceList}
        nameOptions={nameOptions}
        gasSourceOptions={gasSourceOptions}
        filters={filters}
        setters={setters}
      />
      {filteredList.length === 0 ? (
        <div className={styles.empty}>
          <Empty />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <Table
            rowKey={uniqueId()}
            columns={columns}
            dataSource={filteredList}
            pagination={false}
            scroll={{ y: 400, x: 1390 }}
            size="small"
          />
        </div>
      )}
    </Spin>
  );
};

export default DistributionOverview;
